import { useState, useEffect } from "react";
import { X, FileText, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  fetchPTOVersionHistory,
  VersionHistoryEntry,
} from "@service/logsService";
import { formatMDY } from "@utils/ptoHelpers";

export function PTOVersionHistoryDrawer({
  isOpen,
  request,
  onClose,
}: {
  isOpen: boolean;
  request: any;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [history, setHistory] = useState<VersionHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadHistory = async () => {
      if (!request?.id) return;

      setLoading(true);
      setError(null);
      try {
        const historyData = await fetchPTOVersionHistory(request.id);
        setHistory(historyData);
      } catch (err: any) {
        console.error("Failed to load version history:", err);
        setError(
          err?.response?.data?.error || t("ptoVersionHistory.failedToLoad")
        );
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && request?.id) {
      loadHistory();
    }
  }, [isOpen, request?.id, t]);

  if (!isOpen || !request) return null;

  const getOperationColor = (operation: string) => {
    const op = operation.toUpperCase();
    if (op === "INSERT") {
      return "tw-bg-blue-100 tw-text-blue-800";
    }
    if (op === "UPDATE") {
      return "tw-bg-yellow-100 tw-text-yellow-800";
    }
    if (op === "DELETE") {
      return "tw-bg-red-100 tw-text-red-800";
    }
    return "tw-bg-gray-100 tw-text-gray-800";
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex">
      <div className="tw-flex-1 tw-bg-black/40" onClick={onClose} />
      <div className="tw-w-full tw-max-w-2xl tw-bg-white dark:tw-bg-[#0D1117] tw-h-full tw-shadow-xl tw-border-l tw-border-gray-200 dark:tw-border-gray-700 tw-overflow-auto">
        <div className="tw-sticky tw-top-0 tw-bg-white dark:tw-bg-[#0D1117] tw-border-b tw-border-gray-200 dark:tw-border-gray-700 tw-p-4 tw-z-10">
          <div className="tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-gap-2">
              <FileText size={20} />
              {t("ptoVersionHistory.title")}
            </h3>
            <button
              className="tw-p-1 tw-text-gray-500 hover:tw-text-black dark:hover:tw-text-white"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="tw-p-4">
          <div className="tw-bg-gray-50 dark:tw-bg-[#161B22] tw-rounded-lg tw-p-4 tw-mb-4">
            <h4 className="tw-text-sm tw-font-semibold tw-mb-2 tw-text-gray-700 dark:tw-text-gray-300">
              {t("ptoVersionHistory.requestDetails")}
            </h4>
            <div className="tw-space-y-1 tw-text-sm">
              <div>
                <span className="tw-text-gray-500">
                  {t("ptoVersionHistory.id")}:
                </span>{" "}
                <span className="tw-font-mono">#{request.id}</span>
              </div>
              {request.request_type && (
                <div>
                  <span className="tw-text-gray-500">Request Type:</span>{" "}
                  <span className="tw-capitalize">{request.request_type}</span>
                </div>
              )}
              {request.status && (
                <div>
                  <span className="tw-text-gray-500">
                    {t("ptoVersionHistory.status")}:
                  </span>{" "}
                  <span
                    className={`tw-font-medium ${
                      request.status === "Approved" ||
                      request.status === "Balance_Overridden"
                        ? "tw-text-green-600"
                        : request.status === "Rejected"
                        ? "tw-text-red-600"
                        : request.status === "Pending"
                        ? "tw-text-yellow-600"
                        : "tw-text-blue-600"
                    }`}
                  >
                    {t(`pto.statuses.${request.status.toLowerCase()}`, {
                      defaultValue: request.status,
                    })}
                  </span>
                </div>
              )}
              <div>
                <span className="tw-text-gray-500">
                  {t("ptoVersionHistory.dates")}:
                </span>{" "}
                <span>
                  {formatMDY(request.start_date)} →{" "}
                  {formatMDY(request.end_date)}
                </span>
              </div>
              <div>
                <span className="tw-text-gray-500">
                  {t("ptoVersionHistory.hours")}:
                </span>{" "}
                <span>{request.hours_requested}</span>
              </div>
              {request.description && (
                <div>
                  <span className="tw-text-gray-500">
                    {t("ptoVersionHistory.description")}:
                  </span>{" "}
                  <span>{request.description}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="tw-text-sm tw-font-semibold tw-mb-3 tw-text-gray-700 dark:tw-text-gray-300 tw-flex tw-items-center tw-gap-2">
              <Clock size={16} />
              {t("ptoVersionHistory.versionHistory")}
            </h4>

            {loading && (
              <div className="tw-text-center tw-py-8 tw-text-gray-500">
                {t("ptoVersionHistory.loading")}
              </div>
            )}

            {error && (
              <div className="tw-bg-red-50 tw-text-red-700 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-mb-4">
                {error}
              </div>
            )}

            {!loading && !error && history.length === 0 && (
              <div className="tw-text-center tw-py-8 tw-text-gray-500">
                {t("ptoVersionHistory.noHistory")}
              </div>
            )}

            {!loading && !error && history.length > 0 && (
              <div className="tw-space-y-3">
                {history.map((entry, index) => (
                  <div
                    key={entry.transaction_id}
                    className="tw-relative tw-pl-6 tw-pb-3 tw-border-l-2 tw-border-gray-200 dark:tw-border-gray-700 last:tw-border-l-0 last:tw-pb-0"
                  >
                    <div className="tw-absolute tw-left-0 tw-top-0 tw-w-3 tw-h-3 tw-bg-blue-500 tw-rounded-full -tw-translate-x-[7px]" />

                    <div className="tw-bg-white dark:tw-bg-[#0D1117] tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-rounded-lg tw-p-3 tw-shadow-sm">
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-2">
                        <span
                          className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${getOperationColor(
                            entry.operation
                          )}`}
                        >
                          {t(
                            `ptoVersionHistory.operations.${entry.operation.toLowerCase()}`,
                            { defaultValue: entry.operation }
                          )}
                        </span>
                        <span className="tw-text-xs tw-text-gray-500">
                          {entry.timestamp ? entry.timestamp : "N/A"}
                        </span>
                      </div>

                      <div className="tw-text-sm tw-space-y-1">
                        {entry.status && (
                          <div>
                            <span className="tw-text-gray-500">
                              {t("ptoVersionHistory.status")}:
                            </span>{" "}
                            <span
                              className={`tw-font-medium ${
                                entry.status === "Approved" ||
                                entry.status === "Balance_Overridden"
                                  ? "tw-text-green-600"
                                  : entry.status === "Rejected"
                                  ? "tw-text-red-600"
                                  : entry.status === "Pending"
                                  ? "tw-text-yellow-600"
                                  : "tw-text-gray-700 dark:tw-text-gray-300"
                              }`}
                            >
                              {t(`pto.statuses.${entry.status.toLowerCase()}`, {
                                defaultValue: entry.status,
                              })}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="tw-mt-2 tw-text-xs tw-text-gray-500 tw-space-y-0.5">
                        {entry.status_reason && (
                          <div>
                            {t("ptoVersionHistory.statusReason")}:{" "}
                            {entry.status_reason}
                          </div>
                        )}
                        {/* <div>
                          {t("ptoVersionHistory.transactionId")}:{" "}
                          {entry.transaction_id}
                        </div> */}
                        {entry.name && (
                          <div>
                            {t("ptoVersionHistory.by")}: {entry.name}
                          </div>
                        )}
                        {!entry.name && entry.user_id && (
                          <div>
                            {t("ptoVersionHistory.userId")}: {entry.user_id}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
